import React, { useEffect, useRef, useState } from "react";
import { TextField, IconButton, Tooltip, Avatar, Button, Divider, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  addGroupMember,
  deleteGroupMember,
  fetchManagePermissionGroup,
} from "../../../../../utils/api/managePermissionApi";
import { useDispatch } from "react-redux";
import { pushAlert } from "../../../../../utils/alertSlice";
import OwnerDropDown from "../../../../GlobalFeature/Fields/OwnerDropDown";
import { useLazyFetchPersonAllInOrgQuery } from "../../../../../utils/customer/customerTable";
import { safeJsonParse } from "../../../../../utils/utilityFunctions/stringUtility";
import useRequireAuth from "../../../../../utils/useRequireAuth";
import SelectComponent from "../../../../UniversalComponents/SelectComponent";
import Loader from "../../../../UniversalComponents/Loader";

const MemberCard = ({ member, index, getGroupMembers = () => {}, groupData, setRefetch }) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const dispatch = useDispatch();

  const handleRemove = async (userInfo) => {
    try {
      setIsRemoving(true);
      const response = await deleteGroupMember({
        body: {
          group_id: groupData?.group_id,
          group_member_id: userInfo?.user_id,
        },
      });

      if (response?.success) {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
        getGroupMembers();
        setRefetch((prev) => !prev);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div
      key={index}
      className="flex group items-center justify-between bg-[#F9FAFB] border border-gray-200 rounded-md px-3 py-2"
    >
      <div className="flex items-center gap-3">
        <Avatar sx={{ width: 32, height: 32 }}>
          {member?.name
            ?.split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()}
        </Avatar>
        <div className="text-sm">
          <div className="font-medium text-gray-800">{member?.name}</div>
          <div className="text-gray-500 text-xs">{member?.email}</div>
        </div>
      </div>

      <button
        disabled={isRemoving}
        className={`hidden group-hover:block bg-red-100 text-red-700 py-1 px-3 rounded-md`}
        variant="text"
        color="error"
        size="small"
        onClick={() => handleRemove(member)}
        // startIcon={isRemoving && <CircularProgress color="inherit" size={16} thickness={5} />}
      >
        {isRemoving ? "Removing" : "Remove"}
      </button>
    </div>
  );
};

const AddMemberPanel = ({ onClose, groupData, setRefetch, refetch }) => {
  const [emailInput, setEmailInput] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const drawerRef = useRef(null);

  useEffect(() => {
    drawerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [groupData]);

  const companyLocalData = localStorage.getItem("company_data");
  const parsedCompanyData = safeJsonParse(companyLocalData);

  const token = useRequireAuth();

  const [fetchPersonInTable, { data: personTableData }] = useLazyFetchPersonAllInOrgQuery();
  const fetchPersonData = (data) => {
    fetchPersonInTable({ token: token?.token, data: data, companyId: parsedCompanyData?.company_id });
  };

  const handleInsert = async (userId) => {
    try {
      const response = await addGroupMember({
        body: { group_ids: [groupData?.group_id], group_member_ids: [userId] },
      });
      if (response?.success) {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
        getGroupMembers();
        setRefetch((prev) => !prev);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    }
  };

  const getGroupMembers = async () => {
    try {
      setIsLoading(true);
      const response = await fetchManagePermissionGroup({
        query: { key: "group_members", group_id: groupData?.group_id },
      });
      if (response?.success) {
        setGroupMembers(response?.data[0]?.members || []);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    } finally {
      setEmailInput("");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getGroupMembers();
    fetchPersonData();
  }, [groupData]);

  return (
    <div className="flex flex-col border-[1px] border-gray-200 h-full" onClose={onClose}>
      <div className="w-[full] p-4 flex flex-col h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-4" ref={drawerRef}>
          <h2 className="text-[16px] font-medium text-[#1E293B]">
            Add Member <span className="text-gray-500 font-normal">({groupData?.group_name})</span>
          </h2>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Email input */}
        {/* <OwnerDropDown
          fetchPersonInTable={fetchPersonData}
          DropData={personTableData?.data}
          handleInsert={(value) => handleInsert(value)}
          owner={""}
        /> */}

        <SelectComponent
          options={
            personTableData?.data?.map((item) => ({
              label: item?.name,
              value: item?.email,
              id: item?.id,
              subLabel: item?.email,
            })) || []
          }
          onChange={(value) => handleInsert(value?.target?.valueObj[0]?.id)}
          label="Select User"
          placeholder="Select User"
          value={emailInput}
          personList={true}
          showSearch={true}
        />

        {/* Divider */}
        <Divider />

        {/* Existing Members Title */}
        <div className="flex items-center mt-4 mb-2">
          <span className="text-sm font-medium text-gray-700">Existing Members</span>
          <Tooltip title="List of users in Admin Group">
            <InfoOutlinedIcon className="ml-1 text-gray-400" fontSize="small" />
          </Tooltip>
        </div>

        {/* Member List */}
        <div className="flex flex-col space-y-2 overflow-y-auto pr-1">
          {groupMembers?.length > 0 &&
            groupMembers?.map((member, index) => (
              <MemberCard
                member={member}
                index={index}
                key={index}
                getGroupMembers={getGroupMembers}
                groupData={groupData}
                setRefetch={setRefetch}
              />
            ))}

          {groupMembers?.length === 0 && (
            <div className="flex items-center justify-between bg-[#F9FAFB] border border-gray-200 rounded-md px-3 py-2">
              <div className="text-sm text-gray-500">No members in this group</div>
            </div>
          )}
        </div>
      </div>

      <Loader isOpen={isLoading} />
    </div>
  );
};

export default AddMemberPanel;
